import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';

import { SoftwareOnboardingMain } from 'src/components/SoftwareOnboarding';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { type IAgreementUpdateParams } from 'src/graphql/schema';
import { AgreementUpdateDocument } from 'src/graphql/actions/agreement/agreementUpdate.mutation';
import {
  AgreementLegalContextSettings,
  AgreementConditionsSettings,
  AgreementJurisdictionSettings,
  AgreementLiabilitySettings,
} from 'src/containers/Escrow/Agreement';
import { ClientError, getApiError } from 'src/utils/errors';

const AgreementDetails = () => {
  const [updateAgreement] = useMutation(AgreementUpdateDocument);
  const {
    softwareEscrow: { id: escrowId, agreement },
  } = useSoftwareEscrow();

  const onUpdateAgreement = async (agreementParams: IAgreementUpdateParams) => {
    try {
      const { data } = await updateAgreement({
        variables: {
          escrowId,
          agreementParams,
        },
      });

      if (data?.agreementUpdate?.success) {
        toast.success('Saved!');
      } else {
        throw new ClientError(data?.agreementUpdate?.errors?.[0]);
      }
    } catch (error: unknown) {
      getApiError(error, toast.error);
    }
  };

  return (
    <SoftwareOnboardingMain
      subtitle="Agreement information:"
      title="Details"
      noteText="These selections will tailor the escrow agreement to your specific arrangement."
      classes={{
        note: 'not-italic [&>span]:hidden',
      }}
    >
      <div className="w-[580px] max-w-full mx-auto space-y-4">
        <AgreementLegalContextSettings
          onUpdate={onUpdateAgreement}
          onOtherChange={(legalContextOther) => onUpdateAgreement({ legalContextOther })}
          legalContext={agreement?.legalContext}
          legalContextOther={agreement?.legalContextOther ?? ''}
        />
        <AgreementConditionsSettings
          title="Release events"
          description={`Select from the standard terms, or define the specific conditions under which the beneficiary can request the release of the escrow materials.\n\nChoose one, or all that are applicable.`}
          onUpdate={onUpdateAgreement}
          onOtherChange={(conditionsOther) => onUpdateAgreement({ conditionsOther })}
          conditions={agreement?.conditions}
          conditionsOther={agreement?.conditionsOther ?? ''}
        />
        <AgreementJurisdictionSettings
          onUpdate={onUpdateAgreement}
          jurisdiction={agreement?.jurisdiction ?? undefined}
        />
        <AgreementLiabilitySettings onUpdate={onUpdateAgreement} liability={agreement?.liability ?? undefined} />
      </div>
    </SoftwareOnboardingMain>
  );
};

export default AgreementDetails;
